import React, { useRef, useEffect, useMemo, useState } from 'react';
import { Stage, Layer, Transformer, Rect, Line, Image as KonvaImage } from 'react-konva';
import { useEditorStore } from '../store/useEditorStore';
import KonvaTextBox from './KonvaTextBox';
import KonvaSceneObject from './KonvaSceneObject';
import HtmlTextOverlay from './HtmlTextOverlay';
import { konvaBackgroundRectProps } from './konvaBackground';
import {
  extractGcpObjectKeyFromSrc,
  fetchGcpImageBlobUrl,
  fetchGcpImageDataUrl,
  isGcpImageStreamUrl,
} from '../api/gcpImagesApi';

const MIN_BOX = 24;
const MIN_LINE_BOX = 8;
const GRID_STEP = 20;
const MARQUEE_MIN = 4;

function findStageNode(stage, id) {
  if (!stage || !id) return null;
  try {
    return stage.findOne(`#${CSS.escape(String(id))}`) || stage.findOne((n) => n.name?.() === id);
  } catch {
    return stage.findOne((n) => n.name?.() === id || n.id?.() === id);
  }
}

function objectIntersectsRect(obj, rect) {
  const t = obj.transform || {};
  const ox1 = t.x || 0;
  const oy1 = t.y || 0;
  const ox2 = ox1 + Math.max(1, t.width || 0);
  const oy2 = oy1 + Math.max(1, t.height || 0);
  const rx1 = rect.x;
  const ry1 = rect.y;
  const rx2 = rect.x + rect.width;
  const ry2 = rect.y + rect.height;
  return !(ox2 < rx1 || ox1 > rx2 || oy2 < ry1 || oy1 > ry2);
}

/**
 * Konva design surface: scene graph objects + Transformer + HTML text overlay.
 */
export default function KonvaDesignCanvas({
  width,
  height,
  zoomScale = 1,
  onSelectionChange,
  onLayoutChange,
  stageRef: externalStageRef,
  background = null,
  showGrid = false,
}) {
  const bgType = background?.bgType ?? 'solid';
  const bgColor = background?.bgColor ?? '#ffffff';
  const bgGradientFrom = background?.bgGradientFrom ?? '#6366f1';
  const bgGradientTo = background?.bgGradientTo ?? '#a855f7';
  const bgImageUrl = background?.bgImageUrl ?? null;
  const stageRef = useRef(null);
  const containerRef = useRef(null);
  const transformerRef = useRef(null);
  const marqueeRef = useRef({ active: false, startX: 0, startY: 0, x: 0, y: 0, w: 0, h: 0, moved: false });
  const scene = useEditorStore((s) => s.scene);
  const selectedIds = useEditorStore((s) => s.selectedIds);
  const editingTextId = useEditorStore((s) => s.editingTextId);
  const getObjectById = useEditorStore((s) => s.getObjectById);
  const [bgImageEl, setBgImageEl] = useState(null);
  const [marquee, setMarquee] = useState(null);

  const pageW = scene.width || width;
  const pageH = scene.height || height;
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  useEffect(() => {
    useEditorStore.getState().setPageDimensions(width, height);
  }, [width, height]);

  useEffect(() => {
    if (externalStageRef) externalStageRef.current = stageRef.current;
  }, [externalStageRef, scene.objects, width, height]);

  useEffect(() => {
    if (bgType !== 'image' || !bgImageUrl) {
      setBgImageEl(null);
      return undefined;
    }

    let cancelled = false;
    let revokeUrl = null;

    function loadFromUrl(url) {
      return new Promise((resolve) => {
        const img = new window.Image();
        const urlStr = String(url || '');
        if (
          /^https?:\/\//i.test(urlStr) &&
          !urlStr.startsWith('data:') &&
          !urlStr.startsWith('blob:')
        ) {
          img.crossOrigin = 'anonymous';
        }
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = urlStr;
      });
    }

    async function loadBackground() {
      const srcStr = String(bgImageUrl).trim();
      let displayUrl = srcStr;

      if (srcStr.startsWith('data:') || srcStr.startsWith('blob:')) {
        /* use as-is */
      } else if (isGcpImageStreamUrl(srcStr)) {
        const key = extractGcpObjectKeyFromSrc(srcStr);
        if (key) {
          try {
            displayUrl = await fetchGcpImageBlobUrl(key);
            revokeUrl = displayUrl;
          } catch {
            try {
              displayUrl = await fetchGcpImageDataUrl(key);
            } catch {
              /* fall through to direct URL */
            }
          }
        }
      }

      if (cancelled) return;
      const img = await loadFromUrl(displayUrl);
      if (!cancelled) setBgImageEl(img || null);
    }

    loadBackground();

    return () => {
      cancelled = true;
      if (revokeUrl && revokeUrl.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(revokeUrl);
        } catch {
          /* ignore */
        }
      }
    };
  }, [bgType, bgImageUrl]);

  const backgroundRectProps = useMemo(
    () =>
      konvaBackgroundRectProps({
        bgType: bgType === 'image' ? 'solid' : bgType,
        bgColor,
        bgGradientFrom,
        bgGradientTo,
        pageW,
        pageH,
      }),
    [bgType, bgColor, bgGradientFrom, bgGradientTo, pageW, pageH],
  );

  useEffect(() => {
    const tr = transformerRef.current;
    const stage = stageRef.current;
    if (!tr || !stage) return undefined;
    const frame = requestAnimationFrame(() => {
      if (!selectedIds.length || editingTextId) {
        tr.nodes([]);
        tr.getLayer()?.batchDraw();
        return;
      }
      const nodes = selectedIds.map((id) => findStageNode(stage, id)).filter(Boolean);
      tr.nodes(nodes);
      tr.getLayer()?.batchDraw();
    });
    return () => cancelAnimationFrame(frame);
  }, [selectedIds, editingTextId, scene.objects]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    const resolveTextId = (target) => {
      let node = target;
      while (node && node !== stage) {
        const key = node.name?.() || node.id?.();
        if (key) {
          const obj = useEditorStore.getState().getObjectById(key);
          if (obj?.type === 'text') return key;
        }
        node = node.getParent?.();
      }
      return null;
    };
    const onDbl = (e) => {
      const textId = resolveTextId(e.target);
      if (textId) {
        e.cancelBubble = true;
        useEditorStore.getState().startTextEdit(textId);
      }
    };
    stage.on('dblclick dbltap', onDbl);
    return () => {
      stage.off('dblclick dbltap', onDbl);
    };
  }, [scene.objects]);

  useEffect(() => {
    if (!onSelectionChange) return;
    const primaryId = selectedIds[0];
    const obj = primaryId ? getObjectById(primaryId) : null;
    onSelectionChange(obj, selectedIds);
  }, [selectedIds, scene.objects, onSelectionChange, getObjectById]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    const container = stage.container();

    const isTransformerTarget = (target) => {
      let node = target;
      while (node && node !== stage) {
        if (node.getClassName?.() === 'Transformer') return true;
        node = node.getParent?.();
      }
      return false;
    };

    const resolveSceneObject = (target) => {
      let node = target;
      while (node && node !== stage) {
        const key = node.name?.() || node.id?.();
        if (key) {
          const obj = useEditorStore.getState().getObjectById(key);
          if (obj) return obj;
        }
        node = node.getParent?.();
      }
      return null;
    };

    const setCursor = (cursor) => {
      container.style.cursor = cursor;
    };

    const onMouseMove = (e) => {
      if (marqueeRef.current.active) {
        const pos = stage.getPointerPosition();
        if (!pos) return;
        const m = marqueeRef.current;
        m.moved = true;
        m.x = Math.min(m.startX, pos.x);
        m.y = Math.min(m.startY, pos.y);
        m.w = Math.abs(pos.x - m.startX);
        m.h = Math.abs(pos.y - m.startY);
        setMarquee({ x: m.x, y: m.y, width: m.w, height: m.h });
        setCursor('crosshair');
        return;
      }
      if (isTransformerTarget(e.target)) return;
      if (useEditorStore.getState().editingTextId) {
        setCursor('text');
        return;
      }
      const obj = resolveSceneObject(e.target);
      if (!obj) {
        setCursor('default');
        return;
      }
      if (obj.type === 'text') setCursor('text');
      else if (obj.type === 'image') setCursor('pointer');
      else setCursor('move');
    };

    const onDragStart = () => setCursor('grabbing');
    const onDragEnd = () => setCursor('default');

    stage.on('mousemove', onMouseMove);
    stage.on('dragstart', onDragStart);
    stage.on('dragend', onDragEnd);
    return () => {
      stage.off('mousemove', onMouseMove);
      stage.off('dragstart', onDragStart);
      stage.off('dragend', onDragEnd);
      setCursor('default');
    };
  }, [scene.objects]);

  const finishMarquee = (additive) => {
    const m = marqueeRef.current;
    marqueeRef.current = { active: false, startX: 0, startY: 0, x: 0, y: 0, w: 0, h: 0, moved: false };
    setMarquee(null);
    if (m.moved && m.w >= MARQUEE_MIN && m.h >= MARQUEE_MIN) {
      const rect = { x: m.x, y: m.y, width: m.w, height: m.h };
      const hits = scene.objects.filter((obj) => !obj.locked && objectIntersectsRect(obj, rect)).map((o) => o.id);
      if (additive) {
        const merged = [...new Set([...useEditorStore.getState().selectedIds, ...hits])];
        useEditorStore.getState().setSelection(merged);
      } else {
        useEditorStore.getState().setSelection(hits);
      }
      return;
    }
    if (!additive) useEditorStore.getState().clearSelection();
  };

  const gridLines = useMemo(() => {
    if (!showGrid) return null;
    const lines = [];
    for (let x = GRID_STEP; x < pageW; x += GRID_STEP) {
      lines.push(
        <Line
          key={`gv-${x}`}
          points={[x + 0.5, 0, x + 0.5, pageH]}
          stroke="rgba(148, 163, 184, 0.55)"
          strokeWidth={1}
          listening={false}
        />,
      );
    }
    for (let y = GRID_STEP; y < pageH; y += GRID_STEP) {
      lines.push(
        <Line
          key={`gh-${y}`}
          points={[0, y + 0.5, pageW, y + 0.5]}
          stroke="rgba(148, 163, 184, 0.55)"
          strokeWidth={1}
          listening={false}
        />,
      );
    }
    return lines;
  }, [showGrid, pageW, pageH]);

  const sortedObjects = useMemo(
    () => [...scene.objects].sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0)),
    [scene.objects],
  );

  const primarySelected = selectedIds[0] ? getObjectById(selectedIds[0]) : null;
  const imageAspectLocked =
    selectedIds.length === 1 &&
    primarySelected?.type === 'image' &&
    primarySelected?.lockAspectRatio !== false;
  const transformAnchors = useMemo(
    () =>
      imageAspectLocked
        ? ['top-left', 'top-right', 'bottom-left', 'bottom-right']
        : [
            'top-left',
            'top-center',
            'top-right',
            'middle-left',
            'middle-right',
            'bottom-left',
            'bottom-center',
            'bottom-right',
          ],
    [imageAspectLocked],
  );

  useEffect(() => {
    const tr = transformerRef.current;
    if (!tr) return;
    tr.keepRatio(imageAspectLocked);
    tr.enabledAnchors(transformAnchors);
    tr.forceUpdate();
    tr.getLayer()?.batchDraw();
  }, [imageAspectLocked, transformAnchors, selectedIds]);

  return (
    <div
      ref={containerRef}
      className="konva-design-canvas relative block bg-white"
      style={{ width: pageW, height: pageH }}
    >
      <Stage
        ref={stageRef}
        width={pageW}
        height={pageH}
        dragDistance={8}
        onMouseDown={(e) => {
          if (e.target !== e.target.getStage()) return;
          useEditorStore.getState().endTextEdit();
          const stage = e.target.getStage();
          const pos = stage.getPointerPosition();
          if (!pos) return;
          marqueeRef.current = {
            active: true,
            startX: pos.x,
            startY: pos.y,
            x: pos.x,
            y: pos.y,
            w: 0,
            h: 0,
            moved: false,
          };
        }}
        onMouseUp={(e) => {
          if (!marqueeRef.current.active) return;
          const additive = !!(e.evt?.shiftKey || e.evt?.metaKey || e.evt?.ctrlKey);
          finishMarquee(additive);
        }}
      >
        <Layer>
          <Rect {...backgroundRectProps} />
          {bgType === 'image' && bgImageEl ? (
            <KonvaImage image={bgImageEl} x={0} y={0} width={pageW} height={pageH} listening={false} />
          ) : null}
          {sortedObjects.map((node) =>
            node.type === 'text' ? (
              <KonvaTextBox
                key={node.id}
                node={node}
                isSelected={selectedSet.has(node.id)}
                onLayoutChange={onLayoutChange}
              />
            ) : (
              <KonvaSceneObject
                key={node.id}
                node={node}
                isSelected={selectedSet.has(node.id)}
                onLayoutChange={onLayoutChange}
              />
            ),
          )}
          {marquee ? (
            <Rect
              x={marquee.x}
              y={marquee.y}
              width={marquee.width}
              height={marquee.height}
              fill="rgba(42, 49, 139, 0.10)"
              stroke="#2a318b"
              strokeWidth={1}
              dash={[4, 4]}
              listening={false}
            />
          ) : null}
          <Transformer
            ref={transformerRef}
            borderStroke="#2a318b"
            borderStrokeWidth={2}
            anchorStroke="#2a318b"
            anchorStrokeWidth={2}
            anchorFill="#ffffff"
            anchorSize={11}
            anchorCornerRadius={3}
            rotateEnabled={selectedIds.length <= 1}
            keepRatio={imageAspectLocked}
            enabledAnchors={transformAnchors}
            boundBoxFunc={(oldBox, newBox) => {
              const obj = primarySelected;
              const isThinLine =
                obj?.type === 'shape' &&
                (obj.fabricType === 'line' || (obj.fabricType === 'polyline' && obj.closed === false));
              const minW = isThinLine ? MIN_LINE_BOX : MIN_BOX;
              const minH = isThinLine ? 3 : MIN_BOX;
              if (newBox.width < minW || newBox.height < minH) return oldBox;
              if (imageAspectLocked && oldBox.width > 0 && oldBox.height > 0) {
                const aspect = oldBox.width / oldBox.height;
                let width = newBox.width;
                let height = newBox.height;
                const widthDelta = Math.abs(width - oldBox.width);
                const heightDelta = Math.abs(height - oldBox.height);
                if (widthDelta >= heightDelta) {
                  height = width / aspect;
                } else {
                  width = height * aspect;
                }
                if (width < minW || height < minH) return oldBox;
                return { ...newBox, width, height };
              }
              return newBox;
            }}
          />
        </Layer>
        {showGrid && gridLines ? (
          <Layer listening={false}>{gridLines}</Layer>
        ) : null}
      </Stage>
      <HtmlTextOverlay stageContainerRef={containerRef} zoomScale={zoomScale} />
    </div>
  );
}
